import type { GridColDef } from '@mui/x-data-grid-pro';
import { renderExpandableTextCell } from './CellRenderers/ExpandableTextCell';
import { applyColumnDimensions, ColumnDimensionMap } from './columnDimensions';
import { sortByOrderedFields } from './columnOrder';
import type { ExpandableGridColDef } from './gridColumnTypes';

const UNFILTERABLE_FIELDS = ['details', 'detailCTA'];

// MUI paints these itself — a checkmark, an icon row — and a default renderer would paint over it.
const SELF_RENDERING_TYPES = ['boolean', 'actions', 'custom'];

// An untyped column is a string column to MUI, so the default reaches it too.
const isExpandableColumn = (column: GridColDef): boolean =>
  !column.renderCell &&
  !SELF_RENDERING_TYPES.includes(column.type ?? 'string') &&
  !(column as ExpandableGridColDef).disableExpand;

const withExpandableTextCells = <T extends GridColDef>(columns: readonly T[]): T[] =>
  columns.map((column) => (isExpandableColumn(column) ? { ...column, renderCell: renderExpandableTextCell } : column));

export interface PrepareGridColumnsInput<T extends GridColDef> {
  columns: readonly T[];
  hiddenColumns: readonly string[];
  orderedFields?: readonly string[];
  dimensions?: ColumnDimensionMap;
}

const markFilterable = <T extends GridColDef>(columns: readonly T[], hiddenColumns: readonly string[]): T[] =>
  columns.map(
    (column) =>
      ({
        ...column,
        filterable:
          !hiddenColumns.includes(column.field) && !UNFILTERABLE_FIELDS.includes(column.field) && column.filterable
      }) as T
  );

export const prepareGridColumns = <T extends GridColDef>({
  columns,
  hiddenColumns,
  orderedFields,
  dimensions = {}
}: PrepareGridColumnsInput<T>): T[] => {
  const prepared = withExpandableTextCells(applyColumnDimensions(markFilterable(columns, hiddenColumns), dimensions));

  return orderedFields?.length ? sortByOrderedFields(prepared, orderedFields) : prepared;
};
